import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const FalseCreek = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.false-creek-vancouver:About False Creek Paragraph">
          False Creek, nestled in the heart of{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia
          </LinkText>
          , is a vibrant waterfront community that has transformed from
          industrial roots into a picturesque residential and recreational hub.
          This inlet separates downtown Vancouver from the rest of the city,
          offering stunning views of the skyline and North Shore Mountains.
          Residents and visitors alike enjoy the extensive seawall paths,
          bustling markets, and cultural landmarks that define this unique
          neighborhood.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,564 - $1,964',
          bed2: '$2,264 - $2,664',
          bed3: '$3,430 - $3,830'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.false-creek-vancouver:Apartment Hunting False Creek Paragraph 1">
            Finding an apartment for rent in False Creek, Vancouver, BC requires
            careful planning and consideration. Start by determining your
            criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant False Creek lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.false-creek-vancouver:Apartment Hunting False Creek Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long-term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short-term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.false-creek-vancouver:Apartments for rent in False Creek Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in False Creek, Vancouver
              </LinkText>
              , cater to a diverse range of residents, from young professionals
              to retirees. These units often feature modern designs, with many
              offering panoramic waterfront views and easy access to local
              amenities. The neighborhood’s proximity to downtown Vancouver,
              coupled with its serene environment, makes it a sought-after
              location for renters.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.false-creek-vancouver:Houses for rent in False Creek Paragraph">
              <LinkText href={links.house}>
                Houses for rent in False Creek, Vancouver
              </LinkText>
              , are ideal for families and individuals seeking more spacious
              living arrangements. These properties often boast contemporary
              designs and are situated near parks, schools, and recreational
              facilities. The community-oriented atmosphere and convenient
              access to urban amenities make renting a house in this area
              particularly appealing.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.false-creek-vancouver:Townhouses for rent in False Creek Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in False Creek, Vancouver
              </LinkText>{' '}
              , offer a blend of apartment convenience and house-like
              spaciousness. These multi-level homes are perfect for those
              desiring additional space without compromising on urban living
              benefits. Residents can enjoy proximity to the seawall, local
              eateries, and cultural hotspots, enhancing the overall living
              experience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.false-creek-vancouver:Basements for rent in False Creek Paragraph">
              Basements for rent in False Creek, Vancouver, provide affordable
              housing options for students, young professionals, or individuals
              seeking a cost-effective living arrangement. These units typically
              offer private entrances and essential amenities, all within a
              vibrant neighborhood known for its accessibility and scenic
              beauty.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.false-creek-vancouver:Condos for rent False Creek Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in False Creek, Vancouver
              </LinkText>
              , are popular among those seeking modern living spaces with
              upscale amenities. Many condominiums feature facilities such as
              gyms, pools, and concierge services. The strategic location offers
              residents quick access to shopping districts, dining
              establishments, and entertainment venues, making it a prime choice
              for urban dwellers
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.false-creek-vancouver:Rooms for rent in False Creek Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in False Creek, Vancouver
              </LinkText>{' '}
              , are suitable for individuals seeking shared accommodations. This
              option is particularly attractive to students and young
              professionals looking to reside in a vibrant community without the
              higher costs associated with entire apartments or houses. The
              neighborhood’s amenities and public transport connectivity add to
              the convenience of this living arrangement.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.false-creek-vancouver:Short term rentals in False Creek Paragraph">
              <LinkText href={links.shortTerm}>
                Short-term rentals in False Creek, Vancouver
              </LinkText>
              , cater to tourists, business travelers, or those in transition.
              These accommodations range from fully furnished apartments to cozy
              townhouses, providing a home-away-from-home experience. The area’s
              attractions, such as Granville Island and the scenic seawall, make
              it a desirable location for short-term stays.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.false-creek-vancouver:Exploring False Creek Paragraph">
          False Creek is a dynamic neighborhood that seamlessly blends urban
          living with natural beauty. Its transformation from an industrial zone
          to a lively residential area showcases Vancouver’s commitment to
          sustainable urban development. Landmarks like{' '}
          <LinkText href="https://granvilleisland.com/public-market">
            Granville Island Public Market
          </LinkText>{' '}
          and{' '}
          <LinkText href="https://www.scienceworld.ca/">
            Science World at Telus World of Science
          </LinkText>{' '}
          offer cultural and educational experiences. The area’s design
          encourages a lifestyle that appeals to both active individuals and
          those seeking relaxation amidst stunning vistas.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.false-creek-vancouver:Things To Do False Creek Paragraph">
          False Creek in Vancouver is home to numerous landmarks and
          attractions, offering a mix of cultural, recreational, and scenic
          experiences.{' '}
          <LinkText href="https://granvilleisland.com/public-market">
            Granville Island
          </LinkText>{' '}
          is a must-visit, featuring a bustling public market, artisan
          workshops, and theaters.{' '}
          <LinkText href="https://museumofvancouver.ca/">
            Vanier Park houses the Museum of Vancouver
          </LinkText>
          ,{' '}
          <LinkText href="https://www.spacecentre.ca/">
            H.R. MacMillan Space Centre
          </LinkText>
          , and the{' '}
          <LinkText href="https://vanmaritime.com/">
            Vancouver Maritime Museum
          </LinkText>
          .{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/false-creek-olympic-village.aspx">
            The False Creek Seawall and Hinge Park
          </LinkText>{' '}
          provide scenic walking and cycling routes, while{' '}
          <LinkText href="https://vancouver.ca/home-property-development/olympic-village.aspx">
            Olympic Village
          </LinkText>{' '}
          showcases modern urban living. Visitors can also explore{' '}
          <LinkText href="https://covapp.vancouver.ca/parkfinder/parkdetail.aspx?inparkid=241">
            Habitat Island
          </LinkText>
          , ride the False Creek Ferries, or admire the iconic Burrard Street
          Bridge. These attractions make False Creek a vibrant and diverse
          destination.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.false-creek-vancouver:Restaurants False Creek Paragraph">
        <Paragraph>
          False Creek Vancouver offers a range of dining options for every
          taste. Here are three restaurants worth trying.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://www.vancouverdine.com/sandbar/">
            The Sandbar Seafood Restaurant
          </LinkText>{' '}
          - Situated on Granville Island, The Sandbar offers fresh seafood in a
          relaxed, waterfront setting. Guests can enjoy dishes like
          cedar-planked salmon while overlooking the scenic False Creek. The
          ambiance is both lively and cozy, making it ideal for seafood
          enthusiasts seeking a memorable dining experience.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://www.yelp.ca/biz/edible-canada-at-the-market-vancouver">
            Edible Canada Bistro
          </LinkText>{' '}
          - Located near the Granville Island Public Market, Edible Canada
          focuses on Canadian cuisine with a modern twist. Their menu showcases
          locally sourced ingredients, offering dishes such as bison burgers and
          maple-glazed salmon. The bistro’s casual yet chic atmosphere appeals
          to those eager to explore regional flavors.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://www.mahonystavern.com/">
            Mahony & Sons
          </LinkText>{' '}
          - Overlooking the False Creek waterfront, Mahony & Sons is an Irish
          pub offering a fusion of traditional Irish fare and West Coast
          cuisine. Patrons can enjoy classics like shepherd’s pie alongside
          stunning views, making it a favorite spot for both locals and
          tourists.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.false-creek-vancouver:Transportation False Creek Paragraph">
          Getting around False Creek, Vancouver, BC, is convenient with various
          transportation options. The city’s{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , including buses and the{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , provides easy access to surrounding areas. For cyclists, Vancouver
          features an extensive network of bike lanes and rental services. Many
          parts of False Creek are also walkable, with key amenities and
          attractions nearby. Drivers will find a mix of street parking and
          parking garages available. No matter your preferred way of getting
          around, False Creek offers accessible and efficient transportation
          choices.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.false-creek-vancouver:Where To Stay False Creek Paragraph">
          Accommodation options in False Creek, Vancouver cater to those looking
          for a more local and immersive experience. This area offers a mix of
          boutique guesthouses, bed-and-breakfasts, and{' '}
          <LinkText href={links.shortTerm}>short-term rentals</LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, providing
          travelers with a unique stay. While large hotels may be limited within
          False Creek itself, its proximity to other parts of Vancouver ensures
          that a range of traditional accommodations, from luxury to
          budget-friendly, is just a short distance away. Staying in False Creek
          allows visitors to experience its distinct character while remaining
          conveniently close to the city’s main attractions.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default FalseCreek
